import { useEffect, useRef } from "react";
var isPointInElement = function (el, x, y) {
    if (!el)
        return false;
    var rect = el.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};
/** How far the pointer may travel between press and release for the gesture
 * to still count as a click rather than a drag. */
var dragTolerancePx = 5;
/** Marks the app's `ModalRoot`. A modal sits on top of everything, often one
 * opened from the very element this hook guards (an info button in a drawer),
 * so pressing in it — e.g. on its close button — is never a click outside. */
export var modalRootAttributes = { "data-modal-root": "" };
var isInModal = function (target) {
    return target instanceof Element && target.closest("[data-modal-root]") !== null;
};
/**
 * Calls `onOutside` when the user clicks outside all of the given elements.
 *
 * A genuine "click outside" requires both the press (mousedown) and the release
 * (click/mouseup) to land outside every element. This is what distinguishes a
 * click from a drag: a text-selection drag that starts inside an element (e.g.
 * an input field) and is released outside is NOT treated as an outside click, so
 * the drawer stays open. A plain `click` listener can't make this distinction
 * because the browser fires `click` on the common ancestor of the press and
 * release targets with the release coordinates — which look "outside" even
 * though the gesture began inside.
 *
 * A drag that both starts and ends outside does not close either — moving a
 * node or orbiting the camera in the scene beside an open drawer leaves it
 * open. Nor does a click in a modal (see `modalRootAttributes`).
 *
 * Only the press that this hook itself observed can close the element. That
 * means a gesture which *opens* the element (it becomes active on mouseup, as
 * when picking a node in the 3D scene) is not also read as a click outside it,
 * even though the trailing `click` arrives after the listeners are attached.
 */
export var useCloseOnOutsidePointer = function (active, refs, onOutside) {
    // Keep the latest refs/callback in refs so the effect can stay subscribed to
    // `active` alone and not re-attach listeners on every render.
    var refsRef = useRef(refs);
    refsRef.current = refs;
    var onOutsideRef = useRef(onOutside);
    onOutsideRef.current = onOutside;
    var pressRef = useRef();
    useEffect(function () {
        if (!active)
            return;
        var hitsAnyElement = function (x, y) {
            return refsRef.current.some(function (ref) { return isPointInElement(ref.current, x, y); });
        };
        var handleMouseDown = function (event) {
            pressRef.current = {
                x: event.clientX,
                y: event.clientY,
                inside: hitsAnyElement(event.clientX, event.clientY) ||
                    isInModal(event.target),
            };
        };
        var handleClick = function (event) {
            var press = pressRef.current;
            pressRef.current = undefined;
            if (!press)
                return;
            var releasedInside = hitsAnyElement(event.clientX, event.clientY);
            var wasDrag = Math.hypot(event.clientX - press.x, event.clientY - press.y) >
                dragTolerancePx;
            if (!press.inside && !releasedInside && !wasDrag) {
                onOutsideRef.current();
            }
        };
        document.addEventListener("mousedown", handleMouseDown, true);
        document.addEventListener("click", handleClick, true);
        return function () {
            document.removeEventListener("mousedown", handleMouseDown, true);
            document.removeEventListener("click", handleClick, true);
        };
    }, [active]);
};
